import React, {useEffect, useMemo, useRef, useState} from "react"
import {Box, Button, TextField, Typography, useTheme} from "@mui/material";
import {useLocation, useNavigate} from "react-router-dom";
import {clearProfilesMessages, createEntity, updateEntity} from './ProfilesReducer';
import {useSnackbar} from "notistack";
import {RootState} from "../../../Config/Store";
import {useAppDispatch, useAppSelector} from "../../../Config/Hooks";
import {ACard, ADataGrid, ADataGridFilter, ASectionTitle, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {searchEntitiesData} from "./AuthorityReducer";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {columnsData} from "./columnsData";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {
    mapCatalogToRows,
    mergeVisibleSelection,
    RowAuthority,
    selectedAuthorityIdsFromProfile,
} from "./profilesSaveSelection";

const MIN_PERMISSIONS = 1;

const ProfilesSaveSettingsPage = () => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const selectedId = location.state?.id;
    const navigate = useNavigate();
    const isNew = !selectedId;
    const [inputName, setInputName] = useState<string>("");
    const [nameTouched, setNameTouched] = useState<boolean>(false);
    const isNameInvalid = nameTouched && inputName.trim() === "";
    const [isClear, setIsClear] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const authorityEntities = useAppSelector((state : RootState) => state.authority.entities);
    const totalOfRecords = useAppSelector((state : RootState) => state.authority.totalOfRecords);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(columnsData), 0, 1000));
    const entities = useAppSelector((state : RootState) => state.profile.entities);
    const authorityByIdRef = useRef<Map<number, any>>(new Map());
    const initializedForProfileId = useRef<string | number | null>(null);
    const applyingSelectionRef = useRef(false);

    const selectedProfile = useMemo(() => {
        if (!selectedId) {
            return undefined;
        }
        return entities.find((profile) => Number(profile.id) === Number(selectedId));
    }, [selectedId, entities]);

    const [rows, setRows] = useState<Array<RowAuthority>>([]);

    useEffect(() => {
        if (!selectedId) {
            initializedForProfileId.current = null;
            setSelectedIds([]);
            setInputName("");
            return;
        }
        if (!selectedProfile) {
            return;
        }
        if (initializedForProfileId.current == selectedId) {
            return;
        }
        initializedForProfileId.current = selectedId;
        setInputName(selectedProfile.name ?? "");
        setSelectedIds(selectedAuthorityIdsFromProfile(selectedProfile.authorityGroupItemDTOList));
    }, [selectedId, selectedProfile]);

    useEffect(() => {
        for (const authority of authorityEntities) {
            authorityByIdRef.current.set(Number(authority.id), authority);
        }
        applyingSelectionRef.current = true;
        setRows(mapCatalogToRows(authorityEntities, selectedIds));
        setIsClear(true);
        const timeoutId = window.setTimeout(() => {
            applyingSelectionRef.current = false;
            setIsClear(false);
        }, 0);
        return () => window.clearTimeout(timeoutId);
    }, [authorityEntities, selectedIds]);

    const isSaving = useAppSelector((state : RootState) => state.profile.saving);

    const toSaveItems = (ids: number[]) => ids.map((id) => {
        const authority = authorityByIdRef.current.get(Number(id));
        return {
            id: null,
            authorityGroup: null,
            authority: {
                id,
                name: authority?.name,
                displayName: authority?.displayName,
                domainDisplayName: authority?.domain?.displayName
            }
        };
    });

    const saveEntity = async () => {
        if (inputName.trim() === "") {
            setNameTouched(true);
            enqueueSnackbar("O nome do perfil de acesso é obrigatório", {
                variant: 'error'
            });
            return;
        }

        if (selectedIds.length < MIN_PERMISSIONS) {
            enqueueSnackbar("É necessário selecionar pelo menos 1 permissão", {
                variant: 'error',
            });
            return;
        }

        const entity = {
            id: selectedId ? Number(selectedId) : null,
            name: inputName.trim(),
            authorityGroupItemDTOList: toSaveItems(selectedIds)
        };

        try {
            if (selectedId) {
                await dispatch(updateEntity(entity)).unwrap();
            } else {
                await dispatch(createEntity(entity)).unwrap();
            }
            dispatch(clearProfilesMessages());
            enqueueSnackbar(selectedId ? "Perfil de acesso atualizado com sucesso!" : "Perfil de acesso criado com sucesso!", {
                variant: 'success',
            });
            navigate(SystemRoutes.SETTINGS_PROFILE);
        } catch (error: any) {
            dispatch(clearProfilesMessages());
            enqueueSnackbar(error?.detail ?? "Não foi possível salvar o perfil de acesso.", {
                variant: 'error',
            });
        }
    };


    useEffect(() => {
        if (!isSaving) {
            dispatch(searchEntitiesData(pagedSearchParams));
        }
    }, [pagedSearchParams]);

    const handleOnFiltersChange = (dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        let newPageParams :PagedSearchParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(
            newPageParams
        );
    };

    const handleOnRowSelect = (rowsSelected : any) => {
        if (applyingSelectionRef.current) {
            return;
        }
        const visibleIds = rows.map((row) => Number(row.id));
        const visibleSelectedIds = (rowsSelected ?? []).map((row: {id: number}) => Number(row.id));
        setSelectedIds((previousIds) => {
            const nextIds = mergeVisibleSelection(previousIds, visibleIds, visibleSelectedIds);
            if (
                nextIds.length === previousIds.length &&
                nextIds.every((id) => previousIds.includes(id))
            ) {
                return previousIds;
            }
            return nextIds;
        });
    };

    return (
        <Box sx={{marginRight: 5, marginLeft: 3}}>
            <ASectionTitle
                styleContainer={{padding: '0'}}
                styleSubtitle={{marginBottom: 0, paddingTop: 0}}
                styleContainerTitle={{paddingBottom: 0}}
                titleLabel={'Funcionalidades associadas'}
                subTitleLabel={'Gerencie as funcionalidades autorizadas para os usuários que possuem esse perfil de acesso'}
            />
            <TextField
                label="Nome do perfil de acesso"
                variant="outlined"
                fullWidth
                size="small"
                margin="normal"
                required
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                onBlur={() => setNameTouched(true)}
                error={isNameInvalid}
                helperText={isNameInvalid ? "O nome do perfil de acesso é obrigatório" : undefined}
                placeholder={isNew ? "Insira o nome do perfil" : ""}
            />
            <ACard
                ariaLabel={"settings-profiles-form"}
                styleProps={{
                    headerStyle: {height: 0, padding: 0},
                    contentStyle: {marginBottom: 0, padding: 0},
                    cardStyle: {
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px',
                        margin: 0,
                        border: 'none',
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 3,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        height: 64
                    }}
                >
                    <Typography
                        variant="body2"
                        aria-live="polite"
                        sx={{
                            fontWeight: 500,
                            color: selectedIds.length > 0
                                ? theme.palette.text.primary
                                : theme.palette.text.secondary,
                            fontSize: "14px",
                        }}
                    >
                        {selectedIds.length} {selectedIds.length === 1 ? 'funcionalidade associada' : 'funcionalidades associadas'}
                    </Typography>
                </Box>
                <ADataGrid
                    ariaLabel={"settings-profiles-form-grid"}
                    tableContainerStyle={{borderRadius: 0}}
                    columns={columnsData}
                    rows={rows}
                    loading={false}
                    totalOfRecords={totalOfRecords}
                    page={pagedSearchParams.page}
                    onClearSelectedTempRows={isClear}
                    disablePagination={true}
                    rowsPerPage={pagedSearchParams.size}
                    onFiltersChange={handleOnFiltersChange}
                    onRowSelect={handleOnRowSelect}
                />
            </ACard>
            <Box sx={{display: "flex", justifyContent: "flex-end", gap: 2, mt: 2}}>
                <Button
                    variant="text"
                    onClick={() => navigate(SystemRoutes.SETTINGS_PROFILE)}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    onClick={saveEntity}
                    sx={{borderRadius: '8px', paddingX: 4}}
                >
                    Salvar
                </Button>
            </Box>
        </Box>
    );

}

export default ProfilesSaveSettingsPage;
export type {RowAuthority};
